import type { MarketRegionResponseDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { useUpdateRegionMutation } from "Market/Api/Mutation/updateRegionMutation";
import {
	type EditRegionFormSchemaModel,
	getEditRegionFormSchema,
} from "Market/Components/Form/Schema/editRegionFormSchema";
import { useTimezones } from "Market/Hooks/useTimezones";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid2, Stack } from "@mui/material";
import type { FC } from "react";
import { useForm } from "react-hook-form";

type EditRegionFormProps = {
	region: MarketRegionResponseDto;
	handleCancel?: () => void;
};

const marketFormResource = Resources.base.market.forms;

export const EditRegionForm: FC<EditRegionFormProps> = ({
	region,
	handleCancel,
}) => {
	const { t } = useResource();
	const form = useForm<EditRegionFormSchemaModel>({
		resolver: zodResolver(getEditRegionFormSchema()),
		defaultValues: region,
	});

	const {
		control,
		formState: { errors },
	} = form;

	const { mutate: updateRegion, isPending: isPendingUpdate } =
		useUpdateRegionMutation(region.marketId, region.id);

	const { handleServerError } = useHandleServerError(form);

	const handleEditSubmit = () => {
		handleServerError(updateRegion, {
			onSuccess: () => {
				if (handleCancel) {
					handleCancel();
				}
			},
		});
	};

	const timezoneOptions = useTimezones();

	return (
		<>
			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="name"
						name="name"
						label={t(marketFormResource.labels.name)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="regionCode"
						name="regionCode"
						label={t(marketFormResource.labels.regionCode)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						labelTooltip="Region code in format: <country code><united nations - UN/LOCODE>. Example: SKBRA. Follow: https://service.unece.org/trade/locode/gb.htm"
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Select
						id="timezone"
						name="timezone"
						label={t(marketFormResource.labels.timezone)}
						variant="outlined"
						control={control}
						errors={errors}
						options={timezoneOptions}
						fullWidth
					/>
				</Grid2>
			</Grid2>
			<Stack direction="row" spacing={1} mt={2}>
				<Button onClick={handleEditSubmit} loading={isPendingUpdate}>
					{t(marketFormResource.save)}
				</Button>
				{handleCancel && (
					<Button variant="outlined" onClick={handleCancel}>
						{t(marketFormResource.cancel)}
					</Button>
				)}
			</Stack>
		</>
	);
};
